import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    fetchOrgMembers,
    addOrgMember,
    removeOrgMember,
    changeMemberRole
} from 'features/user';
import { useTranslation } from 'react-i18next';
import { FaTrash, FaChevronUp, FaChevronDown, FaUserPlus } from 'react-icons/fa';

const OrgMembers = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const { orgMembers, orgMembersLoading, orgMembersError, user, qr_account } = useSelector(state => state.user);

    const [newUniqueName, setNewUniqueName] = useState('');
    const [newRole, setNewRole] = useState('EDITOR');

    useEffect(() => {
        dispatch(fetchOrgMembers());
    }, [dispatch]);

    // Determine admin status (unchanged logic)
    let isAdmin = false;
    if (qr_account && user) {
        if (qr_account.user === user.id) {
            isAdmin = true;
        } else {
            const myMembership = orgMembers.find(m => m.user_id === user.id);
            if (myMembership && myMembership.role === 'ADMIN') {
                isAdmin = true;
            }
        }
    }

    const handleAdd = e => {
        e.preventDefault();
        if (!newUniqueName.trim()) return;
        dispatch(addOrgMember({ unique_name: newUniqueName.trim(), role: newRole }));
        setNewUniqueName('');
        setNewRole('EDITOR');
    };

    const handleRemove = userId => {
        if (!window.confirm(t('confirm_remove_member'))) return;
        dispatch(removeOrgMember(userId))
            .then(() => dispatch(fetchOrgMembers()));
    };

    const handleRoleToggle = (userId, currentRole) => {
        const newRole = currentRole === 'ADMIN' ? 'EDITOR' : 'ADMIN';
        dispatch(changeMemberRole({ user_id: userId, role: newRole }));
    };

    if (orgMembersLoading && orgMembers.length === 0) {
        return <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>;
    }

    return (
        <div>
            {orgMembersError && <div className="alert alert-danger">{JSON.stringify(orgMembersError)}</div>}

            <div style={{ marginBottom: '12px' }}>
                {orgMembers.map(member => (
                    <div
                        key={member.user_id}
                        style={{
                            backgroundColor: '#ffffff',
                            borderRadius: '8px',
                            padding: '10px 12px',
                            marginBottom: '8px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            border: '1px solid #d4edda',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                        }}
                    >
                        <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: '14px', color: '#2e7d32' }}>
                                {member.first_name} {member.last_name}
                            </div>
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                                {member.email}
                            </div>
                            <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
                                {member.role === 'ADMIN' ? t('admin') : t('editor')}
                            </div>
                        </div>
                        {isAdmin && (
                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                <button
                                    style={{
                                        background: 'transparent',
                                        border: 'none',
                                        color: '#6c757d',
                                        padding: '4px',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => handleRoleToggle(member.user_id, member.role)}
                                    title={member.role === 'EDITOR' ? t('promote_to_admin') : t('demote_to_editor')}
                                >
                                    {member.role === 'EDITOR' ? <FaChevronUp /> : <FaChevronDown />}
                                </button>
                                <button
                                    style={{
                                        background: 'transparent',
                                        border: 'none',
                                        color: '#dc3545',
                                        padding: '4px',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => handleRemove(member.user_id)}
                                    disabled={member.user_id === user?.id}
                                    title={t('remove')}
                                >
                                    <FaTrash />
                                </button>
                            </div>
                        )}
                    </div>
                ))}
                {orgMembers.length === 0 && !orgMembersLoading && (
                    <p style={{ color: '#6c757d', fontSize: '13px', textAlign: 'center', padding: '20px 0' }}>
                        {t('no_members')}
                    </p>
                )}
            </div>

            {isAdmin && (
                <div style={{ borderTop: '1px solid #d4edda', paddingTop: '15px', marginTop: '6px' }}>
                    <form onSubmit={handleAdd}>
                        <input
                            type="text"
                            className="form-control"
                            placeholder={t('TAG')}               // ← “TAG” instead of “unique code”
                            value={newUniqueName}
                            onChange={e => setNewUniqueName(e.target.value)}
                            style={{ height: '38px', fontSize: '13px', borderRadius: '6px', marginBottom: '8px' }}
                            required
                        />
                        <select
                            className="form-select"
                            value={newRole}
                            onChange={e => setNewRole(e.target.value)}
                            style={{ height: '38px', fontSize: '13px', borderRadius: '6px', marginBottom: '8px', width: '100%' }}
                        >
                            <option value="EDITOR">{t('editor')}</option>
                            <option value="ADMIN">{t('admin')}</option>
                        </select>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            style={{
                                width: '100%',
                                height: '38px',
                                fontSize: '14px',
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px',
                            }}
                        >
                            <FaUserPlus style={{ fontSize: '16px' }} />
                            {t('add_member')}
                        </button>
                    </form>
                </div>
            )}
        </div>
    );
};

export default OrgMembers;